import React, { useEffect, useState } from 'react'
import { useBulkLabelGenerationContext } from '../context/bulkLabelGenerationContext'
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'

export const BulkLabelGenerationProgress: React.FC = () => {
  const { currentJob, clearJob } = useBulkLabelGenerationContext()
  const [progress, setProgress] = useState(0)
  const [showDetails, setShowDetails] = useState(false)

  // Simulate progress for running jobs
  useEffect(() => {
    if (currentJob?.status === 'running') {
      const interval = setInterval(() => {
        setProgress(prev => {
          // Slow down as we approach 90%
          if (prev < 90) {
            return Math.min(prev + Math.random() * 5, 90)
          }
          return prev
        })
      }, 1000)

      return () => clearInterval(interval)
    } else if (currentJob?.status === 'completed') {
      setProgress(100)
    }
  }, [currentJob?.status])

  // Auto-hide after completion
  useEffect(() => {
    if (currentJob?.status === 'completed' || currentJob?.status === 'failed') {
      const timeout = setTimeout(() => {
        clearJob()
      }, 10000) // Auto-clear after 10 seconds

      return () => clearTimeout(timeout)
    }
  }, [currentJob?.status, clearJob])

  if (!currentJob) return null

  const getStatusColor = () => {
    switch (currentJob.status) {
      case 'running':
        return 'bg-blue-600'
      case 'completed':
        return 'bg-green-600'
      case 'failed':
        return 'bg-red-600'
      default:
        return 'bg-gray-600'
    }
  }

  const getStatusIcon = () => {
    switch (currentJob.status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />
      case 'failed':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
      default:
        return (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        )
    }
  }

  const getStatusText = () => {
    const entityType = currentJob.entityType || 'episode'
    const pluralSuffix = currentJob.totalEntities !== 1 ? 's' : ''
    const entityName = `${entityType}${pluralSuffix}`
    
    switch (currentJob.status) {
      case 'running':
        return `Generating labels for ${currentJob.totalEntities} ${entityName}...`
      case 'completed':
        const successCount = currentJob.result?.successful_episodes || 
                            currentJob.result?.successful_categories || 
                            currentJob.result?.successful_shows || 
                            currentJob.result?.successful_collections ||
                            currentJob.result?.[`successful_${entityType}s`] ||
                            0
        return `Successfully generated labels for ${successCount}/${currentJob.totalEntities} ${entityName}`
      case 'failed':
        return `Failed to generate labels: ${currentJob.error || 'Unknown error'}`
      default:
        return 'Processing...'
    }
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-800 shadow-lg border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {getStatusIcon()}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {getStatusText()}
                </span>
                {currentJob.status === 'running' && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {Math.round(progress)}%
                  </span>
                )}
              </div>
              {currentJob.status === 'running' && (
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${getStatusColor()}`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              )}
              {currentJob.status === 'completed' && currentJob.result && (
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
                >
                  {showDetails ? 'Hide details' : 'View details'}
                </button>
              )}
            </div>
          </div>
          <button
            onClick={clearJob}
            className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Dismiss"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Details Panel */}
        {showDetails && currentJob.status === 'completed' && currentJob.result && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total {currentJob.entityType}s:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {currentJob.result.total_episodes || 
                   currentJob.result.total_categories || 
                   currentJob.result.total_shows || 
                   currentJob.result.total_collections ||
                   currentJob.totalEntities}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Successful:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {currentJob.result.successful_episodes || 
                   currentJob.result.successful_categories || 
                   currentJob.result.successful_shows || 
                   currentJob.result.successful_collections ||
                   0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Failed:</span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  {currentJob.result.failed_episodes || 
                   currentJob.result.failed_categories || 
                   currentJob.result.failed_shows || 
                   currentJob.result.failed_collections ||
                   0}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
